import { 
  ChallengeTemplate,
  ActiveChallenge,
  ChallengeHistoryItem,
  StartChallengeRequest,
  StartChallengeResponse,
  UpdateChallengeProgressRequest,
  UpdateChallengeProgressResponse,
  GetActiveChallengesResponse,
  GetAvailableChallengesResponse,
  GetChallengeHistoryResponse,
  ChallengeStats
} from '../../types/challenge';

/**
 * API client for challenge system operations
 */
export class ChallengeAPI {

  /**
   * Get active challenges for a couple
   */
  static async getActiveChallenges(coupleId: string): Promise<GetActiveChallengesResponse> {
    try {
      const response = await fetch('/.netlify/functions/getActiveChallenges', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ couple_id: coupleId }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching active challenges:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch active challenges'
      };
    }
  }

  /**
   * Get available challenge templates
   */
  static async getAvailableChallenges(coupleId: string): Promise<GetAvailableChallengesResponse> {
    try {
      const response = await fetch('/.netlify/functions/getAvailableChallenges', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ couple_id: coupleId }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching available challenges:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch available challenges'
      };
    }
  }

  /**
   * Start a new challenge
   */
  static async startChallenge(request: StartChallengeRequest): Promise<StartChallengeResponse> {
    try {
      const response = await fetch('/.netlify/functions/startChallenge', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error starting challenge:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to start challenge'
      };
    }
  }

  /**
   * Update challenge progress
   */
  static async updateChallengeProgress(
    request: UpdateChallengeProgressRequest
  ): Promise<UpdateChallengeProgressResponse> {
    try {
      const response = await fetch('/.netlify/functions/updateChallengeProgress', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error updating challenge progress:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update challenge progress'
      };
    }
  }

  /**
   * Get challenge history for a couple
   */
  static async getChallengeHistory(coupleId: string): Promise<GetChallengeHistoryResponse> {
    try {
      const response = await fetch('/.netlify/functions/getChallengeHistory', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ couple_id: coupleId }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Error fetching challenge history:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch challenge history'
      };
    }
  }

  /**
   * Batch update multiple challenge progresses (useful when adding expenses)
   */
  static async updateMultipleChallengeProgress(
    coupleId: string,
    challengeIds: string[],
    expenseId?: string
  ): Promise<UpdateChallengeProgressResponse[]> {
    const promises = challengeIds.map(challengeId =>
      this.updateChallengeProgress({
        couple_id: coupleId,
        challenge_id: challengeId,
        new_progress: 0, // This will be calculated server-side
        expense_id: expenseId
      })
    );

    try {
      const results = await Promise.allSettled(promises);
      return results.map(result => 
        result.status === 'fulfilled' 
          ? result.value 
          : { success: false, error: 'Failed to update challenge progress' }
      );
    } catch (error) {
      console.error('Error updating multiple challenge progress:', error);
      return challengeIds.map(() => ({
        success: false,
        error: 'Failed to update challenge progress'
      }));
    }
  }
}

/**
 * Helper functions for challenge data processing
 */
export class ChallengeHelpers {

  /**
   * Sort challenges by priority (active first, then by end date)
   */
  static sortChallengesByPriority(challenges: ActiveChallenge[]): ActiveChallenge[] {
    return [...challenges].sort((a, b) => {
      // Active challenges first
      if (a.status === 'active' && b.status !== 'active') return -1;
      if (b.status === 'active' && a.status !== 'active') return 1;
      
      // Then by end date (soonest first)
      return new Date(a.end_date).getTime() - new Date(b.end_date).getTime();
    });
  }

  /**
   * Filter challenges by status
   */
  static filterChallengesByStatus(
    challenges: ActiveChallenge[], 
    status: string
  ): ActiveChallenge[] {
    return challenges.filter(challenge => challenge.status === status);
  }

  /**
   * Group challenges by difficulty
   */
  static groupChallengesByDifficulty(
    templates: ChallengeTemplate[]
  ): Record<string, ChallengeTemplate[]> {
    return templates.reduce((groups, template) => {
      const difficulty = template.difficulty;
      if (!groups[difficulty]) {
        groups[difficulty] = [];
      }
      groups[difficulty].push(template);
      return groups;
    }, {} as Record<string, ChallengeTemplate[]>);
  }

  /**
   * Calculate completion rate from history
   */
  static calculateCompletionRate(history: ChallengeHistoryItem[]): number {
    if (history.length === 0) return 0;
    
    const completed = history.filter(item => item.status === 'completed').length;
    return Math.round((completed / history.length) * 100);
  }

  /**
   * Get challenge statistics
   */
  static calculateChallengeStats(history: ChallengeHistoryItem[]): ChallengeStats {
    const totalCompleted = history.filter(item => item.status === 'completed').length;
    const totalFailed = history.filter(item => item.status === 'failed').length;
    const totalPointsEarned = history.reduce((sum, item) => sum + item.points_earned, 0);
    const completionRate = history.length > 0 ? (totalCompleted / history.length) * 100 : 0;

    // Current month stats
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();
    const currentMonthHistory = history.filter(item => {
      const completedDate = new Date(item.completed_date || item.end_date);
      return completedDate.getMonth() === currentMonth && 
             completedDate.getFullYear() === currentYear;
    });
    
    const currentMonthCompleted = currentMonthHistory.filter(
      item => item.status === 'completed'
    ).length;
    const currentMonthPoints = currentMonthHistory.reduce(
      (sum, item) => sum + item.points_earned, 0
    );

    // Most common challenge type
    const challengeTypeCounts = history.reduce((counts, item) => {
      counts[item.challenge_type] = (counts[item.challenge_type] || 0) + 1;
      return counts;
    }, {} as Record<string, number>);
    
    const favoriteType = Object.entries(challengeTypeCounts).reduce(
      (max, [type, count]) => count > max.count ? { type: type as any, count } : max,
      { type: null, count: 0 }
    ).type;

    return {
      total_completed: totalCompleted,
      total_failed: totalFailed,
      total_points_earned: totalPointsEarned,
      completion_rate: Math.round(completionRate * 100) / 100,
      current_month_completed: currentMonthCompleted,
      current_month_points: currentMonthPoints,
      favorite_challenge_type: favoriteType,
      longest_streak: 0 // Would need separate streak tracking
    };
  }

  /**
   * Format time remaining text
   */
  static formatTimeRemaining(daysRemaining: number): string {
    if (daysRemaining === 0) {
      return 'Ends today';
    } else if (daysRemaining === 1) {
      return '1 day left';
    } else if (daysRemaining < 7) {
      return `${daysRemaining} days left`;
    } else if (daysRemaining < 14) {
      return `${Math.ceil(daysRemaining / 7)} week left`;
    } else {
      return `${Math.ceil(daysRemaining / 7)} weeks left`;
    }
  }

  /**
   * Get progress color based on percentage and challenge type
   */
  static getProgressColor(challenge: ActiveChallenge): string {
    const percentage = challenge.progress_percentage;
    const isSpendingLimit = challenge.challenge_type === 'spending_limit' || 
                           challenge.challenge_type === 'category_limit';

    if (isSpendingLimit) {
      // For spending limits, lower is better
      if (percentage <= 50) return 'text-green-600';
      if (percentage <= 80) return 'text-yellow-600';
      return 'text-red-600';
    } else {
      // For other challenges, higher is better
      if (percentage >= 80) return 'text-green-600';
      if (percentage >= 50) return 'text-yellow-600';
      return 'text-red-600';
    }
  }

  /**
   * Get difficulty badge variant
   */
  static getDifficultyVariant(difficulty: string): 'secondary' | 'default' | 'destructive' {
    switch (difficulty.toLowerCase()) {
      case 'easy': return 'secondary';
      case 'medium': return 'default';
      case 'hard': return 'destructive';
      default: return 'default';
    }
  }

  /**
   * Format challenge progress text
   */
  static formatProgressText(challenge: ActiveChallenge): string {
    switch (challenge.challenge_type) {
      case 'spending_limit':
        return `$${challenge.current_progress.toFixed(2)} / $${challenge.target_amount?.toFixed(2) || '0'}`;
      
      case 'category_limit':
        return `$${challenge.current_progress.toFixed(2)} / $${challenge.target_amount?.toFixed(2) || '0'}`;
      
      case 'savings_goal':
        return `$${challenge.current_progress.toFixed(2)} / $${challenge.target_amount?.toFixed(2) || '0'} saved`;
      
      case 'no_spend_days':
        return `${challenge.current_progress} / ${challenge.target_days || 0} days`;
      
      case 'streak_goal':
        return `${challenge.current_progress} / ${challenge.target_days || 0} day streak`;
      
      default:
        return `${challenge.progress_percentage.toFixed(1)}% complete`;
    }
  }
}